import { customElement, state } from 'lit/decorators.js';

import JobSchema from '../../../../../models/job-schema.js';
import Company from '../../../../../models/company.js';
import { createLogger } from '../../../../../ns-services/logger.js';

import NSBankFormJob, { BankFormCopy } from './bank-form-job.js';
import { bankValidationDetails, formatSortCode, type BankDetails } from './bank-details.js';
import definition from '@job-schemas/company/bank/transfer.json';
import { jobSchema } from '../../../job-schema.js';

const logger = createLogger('ns-company-bank-transfer');

interface FormValues {
  reference: string;
  bankName: string;
}

@customElement(NSCompanyBankTransfer.is)
export default class NSCompanyBankTransfer extends NSBankFormJob<FormValues> {
  static get is() {
    return 'ns-company-bank-transfer';
  }

  // The company being verified and the account it added in Connect to Your Bank: the account the transfer goes to
  @state()
  private __connector?: Company;

  @state()
  private __bankDetails?: BankDetails;

  @state()
  private __bankDetailsLoaded = false;

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { complete: { type: 'hidden' } } });
  }

  async connectedCallback() {
    await super.connectedCallback();
    try {
      // Read from the company itself (Support can read company records), so the account is never copied elsewhere
      this.__connector = await this.loadInput<Company>('company', 'connectorCompanyId');
      this.__bankDetails = bankValidationDetails(this.__connector.companiesRegistrar);
    } catch (err) {
      // A transfer job made before the workflow passed the company on has no connectorCompanyId
      logger.warn('Unable to load the account to transfer to', err);
    } finally {
      this.__bankDetailsLoaded = true;
    }
  }

  protected get _copy(): BankFormCopy<FormValues> {
    const unavailable = this.__bankDetailsLoaded && !this.__bankDetails?.accountNumber;
    return {
      intro:
        "Make a low-value bank transfer, outside nodeStream, to the account below, using a unique reference of your choice. Then enter that reference so the organisation's director can confirm they control the account.",
      details: {
        label: 'Account to transfer to',
        unavailable: unavailable
          ? "The account details couldn't be loaded. Please check the company's bank details before making the transfer."
          : undefined,
        rows: [
          { label: 'Company', value: this.__connector?.name },
          { label: 'Account Holder', value: this.__bankDetails?.name },
          { label: 'Account Number', value: this.__bankDetails?.accountNumber },
          { label: 'Sort Code', value: formatSortCode(this.__bankDetails?.sortCode) },
        ],
      },
      heading: 'Enter the transfer reference',
      fields: [
        {
          name: 'reference',
          label: 'Reference',
          requiredMessage: 'Please enter the reference used for the bank transfer',
        },
        { name: 'bankName', label: 'Bank Name' },
      ],
      lambda: 'bank/add-transfer-reference',
      loadingMessage: 'Saving the transfer reference',
      info: [
        {
          label: 'How the account is verified',
          steps: [
            'You make a low-value transfer to the account shown, outside nodeStream, with a unique reference of your choice.',
            'You enter that reference here.',
            'The person who added the bank details finds the reference on their bank statement and enters it in the next step, to confirm they control the account.',
          ],
        },
        {
          label: 'Bank name',
          text: "Optional. If your bank shows it when you make the transfer, add it here and it's saved to the company's bank record.",
        },
      ],
    };
  }

  protected _requestBody(values: Partial<FormValues>) {
    return { reference: values.reference, bankName: values.bankName };
  }
}
